import { Percent } from '@pancakeswap/sdk'

/**
 * Price impact, MultiSwap definition (Round 3):
 *
 *   spotOut      = amountIn × Π(pre-trade pool price of hop i)        — the route's mid price BEFORE the order
 *   impactRaw    = (spotOut − grossOut) / spotOut                     — execution vs pre-trade mid price
 *   impact       = impactRaw − lpFeeShare                             — the LP fee is reported separately
 *                                                                        (feePercent) and removed here, same
 *                                                                        convention as the standard swap's
 *                                                                        `priceImpactWithoutFee`
 *
 * The base is never the order's own average execution price, and the deviation from an external
 * reference price (e.g. PancakeSwap vs CADINU spot) is not part of it. Protocol fee: not included
 * (grossOut is the router output before the protocol fee).
 */
export interface ImpactResult {
  /** (spotOut − grossOut) / spotOut, including the LP fee's share. */
  raw: Percent
  /** raw − lpFeeShare (floored at 0). */
  withoutFee: Percent
  spotOut: bigint
}

const Q96 = 2n ** 96n
const Q192 = Q96 * Q96

/** V2: pre-trade spot output through hops of reserves (reserveIn, reserveOut). x·y = k mid price = rOut / rIn. */
export function spotOutV2(hops: { reserveIn: bigint; reserveOut: bigint }[], amountIn: bigint): bigint {
  let num = amountIn
  let den = 1n
  for (const h of hops) {
    if (h.reserveIn <= 0n || h.reserveOut <= 0n) return 0n
    num *= h.reserveOut
    den *= h.reserveIn
  }
  return num / den
}

/** V3: pre-trade spot output through hops of slot0.sqrtPriceX96; zeroForOne = tokenIn is token0. */
export function spotOutV3(hops: { sqrtPriceX96: bigint; zeroForOne: boolean }[], amountIn: bigint): bigint {
  let num = amountIn
  let den = 1n
  for (const h of hops) {
    if (h.sqrtPriceX96 <= 0n) return 0n
    const p2 = h.sqrtPriceX96 * h.sqrtPriceX96 // price1/0 × 2^192
    if (h.zeroForOne) {
      num *= p2
      den *= Q192
    } else {
      num *= Q192
      den *= p2
    }
  }
  return num / den
}

export function priceImpact(spotOut: bigint, grossOut: bigint, lpFeeShare: Percent): ImpactResult | null {
  if (spotOut <= 0n) return null
  const rawNum = spotOut > grossOut ? spotOut - grossOut : 0n
  const raw = new Percent(rawNum, spotOut)
  const diff = raw.subtract(lpFeeShare)
  const withoutFee = diff.lessThan(new Percent(0n, 1n)) ? new Percent(0n, 1n) : diff
  return { raw, withoutFee, spotOut }
}

/** x·y = k exact-input output for one V2 hop (PancakeSwap/CADINU: 0.25 % = 9975/10000). */
export function v2AmountOut(amountIn: bigint, reserveIn: bigint, reserveOut: bigint, feeBps = 25n): bigint {
  const inWithFee = amountIn * (10_000n - feeBps)
  return (inWithFee * reserveOut) / (reserveIn * 10_000n + inWithFee)
}
